import { Link } from 'react-router';
import { sortBy } from '../../../shared/utils/sortUtils.ts';
import { Batch, Operation } from '../../types/sharedTypeImpl.ts';
import { countWorkdays } from '../../utils/countWorkdays.ts';
import { formatBrokenToolFrequency, formatDatetime } from '../../utils/fieldFormats.ts';
import { countActiveToolChangeAlarms } from '../../utils/toolChangeAlarms.ts';
import { trpc } from '../../utils/trpc/trpc.ts';


interface Props {
    selectedBatch: Batch | null;
    onBatchSelected: (batch: Batch, batchOps: Operation[]) => void;
}

export function BrokenToolsPerBatch({
    selectedBatch,
    onBatchSelected,
}: Props) {

    const { data: batches } = trpc.batches.getAllCurrent.useQuery(
        undefined,
        { select: batches => sortBy([...batches], it => it.equipmentDisplayName) }
    );

    const batchOperations = trpc.useQueries((t) =>
        batches?.map(batch => t.operations.get({
            equipment: batch.equipment,
            effectiveTime: { start: batch.start, end: batch.end },
            operationTypes: ['Tool change', 'Fix unscheduled tool change'],
        }, {
            refetchOnWindowFocus: false,
        })) ?? []
    );


    const handleRowClick = (batch: Batch, index: number) => {
        if (!batchOperations[index].data) {
            return
        }
        onBatchSelected(batch, batchOperations[index].data!);
    };

    return (
        <table className='table--mid-width'>
            <thead>
                <tr>
                    <th>Equipment</th>
                    <th>Part number</th>
                    <th>Start</th>
                    <th>Unresolved count</th>
                    <th>Interval (days)</th>
                </tr>
            </thead>
            <tbody>
                {batches?.map((batch, idx) => {
                    const isSelected = selectedBatch?.equipment === batch.equipment;
                    const operations = batchOperations[idx].data ?? null;
                    const alarmCount = operations
                        ? countActiveToolChangeAlarms(operations)
                        : null;
                    const frequency = alarmCount
                        ? countWorkdays(batch.start, new Date()) / +alarmCount
                        : null;

                    return (
                        <tr
                            key={idx}
                            className={isSelected ? 'highlight--selected' : ''}
                            onClick={() => handleRowClick(batch, idx)}
                        >
                            <td className='textAlignLeft'>
                                <Link to={`/machine-log#equipment=${batch.equipment}`} className='link-button--non-intrusive'>
                                    {batch.equipmentDisplayName}
                                </Link>
                            </td>
                            <td title={batch.product}>{batch.partDisplayName}</td>
                            <td>{formatDatetime(batch.start)}</td>
                            <td>{alarmCount ?? '...'}</td>
                            <td>{formatBrokenToolFrequency(frequency)}</td>
                        </tr>
                    )
                })}
            </tbody>
        </table>
    );
}
